/**
 * 关键指标趋势图组件
 * 包含：Mentions/Engagement 卡片 + 双轴趋势图（声量 + 声量占比）
 */
import React, { useMemo, useContext } from 'react';
import CardCommon from '@op/components/CardCommon';
import useLanguage from '@op/hooks/useLanguage';
import { DBO_TM, DBO_V } from '@op/utils/format';
import { formatDateFunc } from '@op/utils/time';
import RingRatioBubble from '@op/components/Snippets/RingRatioBubble';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import KeyMetricsTrendChart from './KeyMetricsTrendChart';
import { useOverviewData } from '../../hooks/useOverviewData';
import { SecurityWeeklyReportContext } from '../../context';
import './index.scss';

interface IKeyMetricsChartProps {
  filter: any;
}

const KeyMetricsChart: React.FC<IKeyMetricsChartProps> = ({ filter }) => {
  const { toFeeds } = useContext(SecurityWeeklyReportContext);
  const { formatMessage } = useLanguage();
  // 获取概览数据（包含 summary 指标数据 + chartData 用于图表和下载）
  const {
    data: overviewData,
    chartData,
    linkTime,
    loading,
  } = useOverviewData(filter);

  const list = chartData?.tableRecord || [];
  const empty = useMemo(() => !loading && list.length === 0, [loading, list]);

  // 当前周期时间范围（从 Filter 获取）
  const currentTimeRange = [filter?.start_time, filter?.end_time];
  // 渲染指标卡片 - 使用 RingRatioBubble + PercentageItem 展示环比
  const renderMetricCard = (
    title: string,
    subtitle: string,
    value: number,
    changeRate: number,
    prevValue: number,
    newTime?: string[],
    oldTime?: string[],
  ) => {
    return (
      <div className="dbo-metric-card">
        <div className="dbo-metric-card__header">
          <span className="dbo-metric-card__title">{title}</span>
          <span className="dbo-metric-card__subtitle">({subtitle})</span>
        </div>
        <div className="dbo-metric-card__value">
          {DBO_V.formatThousand(value)}
        </div>
        <div className="dbo-metric-card__change">
          <span className="dbo-metric-card__change-label">
            {formatMessage({
              id: DBO_TM.qoqTxt(
                filter.date_type,
                filter.start_time,
                filter.end_time,
              ).text,
            })}
          </span>
          <RingRatioBubble
            title={title}
            oldTime={oldTime || []}
            newTime={newTime || []}
            oldValue={prevValue}
            newValue={value}
            formateDate={formatDateFunc(filter?.date_type)}
            formateValue={(v) => DBO_V.reviews(v)}
            placement="top"
          >
            <PercentageItem
              percentage={changeRate * 100}
              className="dbo-metric-card__change-value--security"
              formateFunc={(value) => DBO_V.percentageFormat(value, '-', 1)}
            />
          </RingRatioBubble>
        </div>
      </div>
    );
  };

  return (
    <CardCommon className="dbo-key-metrics-section">
      {/* 左右布局：卡片 + 趋势图 */}
      <div className="dbo-key-metrics-section__content">
        {/* 左侧指标卡片区域 */}
        <div className="dbo-key-metrics-section__cards">
          <CardCommon
            className="dbo-key-metrics-section__card"
            loading={loading}
            loadingHeight={150}
            logBtnId="WR_Mention"
            contentCard
          >
            {overviewData &&
              renderMetricCard(
                formatMessage({ id: 'Mentions' }),
                formatMessage({ id: 'Security-Related Mentions' }),
                overviewData.mention,
                overviewData.mention_change_rate,
                overviewData.prev_mention,
                currentTimeRange,
                linkTime ? [linkTime.start_time, linkTime.end_time] : [],
              )}
          </CardCommon>

          <CardCommon
            className="dbo-key-metrics-section__card"
            loading={loading}
            loadingHeight={150}
            logBtnId="WR_Engagement"
            contentCard
          >
            {overviewData &&
              renderMetricCard(
                formatMessage({ id: 'Engagement' }),
                formatMessage({
                  id: 'Engagements (Likes + Shares + Comments)',
                }),
                overviewData.engagement,
                overviewData.engagement_change_rate,
                overviewData.prev_engagement,
                currentTimeRange,
                linkTime ? [linkTime.start_time, linkTime.end_time] : [],
              )}
          </CardCommon>
        </div>

        {/* 右侧趋势图 */}
        <KeyMetricsTrendChart
          className="dbo-key-metrics-section__chart"
          data={chartData}
          height={280}
          loading={loading}
          empty={empty}
          toFeeds={toFeeds}
          filter={filter}
        />
      </div>
    </CardCommon>
  );
};

export default KeyMetricsChart;
